import { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Modal from '../modal/modal';
import Card from '../card/Card';
import P from '../texts/p/P';
import Button from '../button/Button';
import Input from '../input/Input';
import Icon from '../icon/Icon';
import Spinner from '../Spinner/spinner';
import Span from '../texts/span/Span';
import { useEconomicGroups } from '../../hooks/useEconomicGroups';
import { EconomicGroup } from '../../types/interfaces';
import { getEconomicGroups } from '../../services/EconomicGroupService';

interface EconomicGroupManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGroupUpdated?: () => void;
}

interface GroupFormData {
  name: string;
  code: string;
}

const ScrollableContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
`;

const TableRow = styled.div<{ isEditing?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s;
  background-color: ${({ isEditing }) => (isEditing ? '#f5f5f5' : 'transparent')};

  &:hover {
    background-color: ${({ isEditing }) => (isEditing ? '#f5f5f5' : '#fafafa')};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-bottom: 2px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const GroupInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const GroupNameText = styled(P)`
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding: 0;
  border: 1px solid ${theme.colors.primary.border.regular};
  border-radius: ${theme.borderRadius['border-radius-3']};
  background-color: ${theme.colors.neutral.bg.inverted};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.primary.bg.weak};
    border-color: ${theme.colors.primary.border.strong};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: ${theme.colors.neutral.border.weak};
  }

  &.delete-button:hover:not(:disabled) {
    background-color: ${theme.colors.danger.bg.weak || '#FFE6E6'};
    border-color: ${theme.colors.danger.border.regular};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #999;
`;

const FormContainer = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
`;

const ListContainer = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const EconomicGroupManagementModal: React.FC<EconomicGroupManagementModalProps> = ({
  isOpen,
  onClose,
  onGroupUpdated,
}) => {
  const {
    economicGroups,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    createGroup,
    updateGroup,
    removeGroup,
    refreshEconomicGroups,
  } = useEconomicGroups();

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<GroupFormData>({ name: '', code: '' });
  const [formErrors, setFormErrors] = useState<{ name?: string; code?: string }>({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  const generateNextCode = (groupsList?: EconomicGroup[], startFrom?: number): string => {
    const listToUse = groupsList || economicGroups;

    if (listToUse.length === 0) return '001';

    const existingCodes = new Set(
      listToUse
        .map((g) => {
          const num = parseInt(g.code, 10);
          return isNaN(num) ? 0 : num;
        })
        .filter((num) => num > 0),
    );

    if (existingCodes.size === 0) return '001';

    const maxCode = Math.max(...Array.from(existingCodes));
    let nextCode = startFrom || maxCode + 1;

    while (existingCodes.has(nextCode) && nextCode < 999) {
      nextCode++;
    }

    return nextCode.toString().padStart(3, '0');
  };

  useEffect(() => {
    if (!isOpen) {
      setShowNewForm(false);
      setEditingId(null);
      setDeletingId(null);
      setFormData({ name: '', code: '' });
      setFormErrors({});
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredGroups = economicGroups.filter((group) => {
    if (!searchTerm.trim()) return true;
    return group.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    if (editingId !== null) {
      const group = economicGroups.find((g) => g.id === editingId);
      if (group) {
        setFormData({ name: group.name, code: group.code });
        setFormErrors({});
      }
    }
  }, [editingId, economicGroups]);

  const validateForm = (): boolean => {
    const errors: { name?: string; code?: string } = {};
    if (!formData.name.trim()) {
      errors.name = 'El nombre es obligatorio';
    }
    if (editingId !== null && !formData.code.trim()) {
      errors.code = 'El código es obligatorio';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNewGroup = () => {
    const nextCode = generateNextCode();
    setShowNewForm(true);
    setEditingId(null);
    setFormData({ name: '', code: nextCode });
    setFormErrors({});
  };

  const handleEditGroup = (group: EconomicGroup) => {
    setEditingId(group.id);
    setShowNewForm(false);
    setFormData({ name: group.name, code: group.code });
    setFormErrors({});
  };

  const handleCancelForm = () => {
    setShowNewForm(false);
    setEditingId(null);
    setFormData({ name: '', code: '' });
    setFormErrors({});
  };

  const handleSaveGroup = async () => {
    if (!validateForm()) return;

    let codeToSave =
      showNewForm && !editingId ? formData.code || generateNextCode() : formData.code.trim();

    if (showNewForm && !editingId) {
      const maxRetries = 5;
      let retries = 0;
      let result = await createGroup(formData.name.trim(), codeToSave);

      while (
        !result.success &&
        ((result as any).status === 409 || (result as any).code === 'DUPLICATE_CODE') &&
        retries < maxRetries
      ) {
        retries++;
        await refreshEconomicGroups();

        const updatedListResponse = await getEconomicGroups();
        const previousCodeNum = parseInt(codeToSave, 10);
        const startFrom = isNaN(previousCodeNum) ? undefined : previousCodeNum + 1;

        if (updatedListResponse.success && updatedListResponse.data) {
          codeToSave = generateNextCode(updatedListResponse.data, startFrom);
        } else {
          codeToSave = generateNextCode(undefined, startFrom);
        }

        setFormData({ ...formData, code: codeToSave });
        result = await createGroup(formData.name.trim(), codeToSave);
      }

      if (result.success) {
        handleCancelForm();
        if (onGroupUpdated) onGroupUpdated();
      }
    } else {
      const result = await updateGroup(editingId!, formData.name.trim(), codeToSave);
      if (result.success) {
        handleCancelForm();
        if (onGroupUpdated) onGroupUpdated();
      }
    }
  };

  const handleDeleteClick = (id: number) => {
    setDeletingId(id);
  };

  const handleConfirmDelete = async () => {
    if (deletingId === null) return;
    const result = await removeGroup(deletingId);
    if (result.success) {
      setDeletingId(null);
      if (onGroupUpdated) onGroupUpdated();
    }
  };

  const handleCancelDelete = () => {
    setDeletingId(null);
  };

  const handleClose = () => {
    if (!isCreating && !isUpdating && !isDeleting) {
      handleCancelForm();
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title='Gestionar grupos económicos'
      primaryVariant='primary'
      primaryText='Cerrar'
      onPrimaryAction={handleClose}
      primaryActionDisabled={isCreating || isUpdating || isDeleting}
      width='700px'
      overflow='hidden'
    >
      <Card display='grid' gap='16px' style={{ maxHeight: '70vh', overflow: 'hidden' }}>
        {isLoading ? (
          <Card display='flex' justifyContent='center' margin={{ top: '4', bottom: '4' }}>
            <Spinner size='md' variant='primary' />
          </Card>
        ) : (
          <>
            <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '2' }}>
              <Card display='flex' flexDirection='column' gap='4px'>
                <P type='1' variant='neutral-1' weight='bold' withMargin={false}>
                  Lista de grupos económicos
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  {filteredGroups.length} de {economicGroups.length}{' '}
                  {economicGroups.length === 1 ? 'grupo económico' : 'grupos económicos'}
                </P>
              </Card>
              {!showNewForm && editingId === null && (
                <Button variant='primary' size='md' onClick={handleNewGroup}>
                  <Icon name='add_outline' variant='neutral' modifier='inverted' width={18} height={18} />
                  Nuevo grupo económico
                </Button>
              )}
            </Card>

            {!showNewForm && editingId === null && economicGroups.length > 0 && (
              <Card margin={{ bottom: '2' }}>
                <Input
                  label=''
                  id='search-economic-groups'
                  placeholder='Buscar por nombre...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icons={{ iconLeft: 'search_outline' }}
                  autocomplete='off'
                />
              </Card>
            )}

            {(showNewForm || editingId !== null) && (
              <FormContainer padding='4'>
                <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ bottom: '3' }}>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    {editingId ? 'Editar grupo económico' : 'Nuevo grupo económico'}
                  </P>
                  <Button
                    variant='tertiary'
                    size='sm'
                    onClick={handleCancelForm}
                    disabled={isCreating || isUpdating}
                  >
                    <Icon name='cancel_outline' variant='neutral' height={18} />
                  </Button>
                </Card>
                <Card display='grid' gap='16px'>
                  <Card display='grid' gap='8px'>
                    <Input
                      label='Nombre *'
                      id='economic-group-name-input'
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                      }}
                      inputStatus={formErrors.name ? 'error' : 'normal'}
                      maxLength={300}
                      placeholder='Ingrese el nombre del grupo económico'
                    />
                    {formErrors.name && (
                      <Span variant='danger' type='2'>
                        {formErrors.name}
                      </Span>
                    )}
                  </Card>
                  {editingId !== null && (
                    <Card display='grid' gap='8px'>
                      <Input
                        label='Código *'
                        id='economic-group-code-input'
                        value={formData.code}
                        onChange={(e) => {
                          setFormData({ ...formData, code: e.target.value });
                          if (formErrors.code) setFormErrors({ ...formErrors, code: undefined });
                        }}
                        inputStatus={formErrors.code ? 'error' : 'normal'}
                        maxLength={50}
                        placeholder='Ingrese el código del grupo económico'
                      />
                      {formErrors.code && (
                        <Span variant='danger' type='2'>
                          {formErrors.code}
                        </Span>
                      )}
                    </Card>
                  )}
                  <Card display='flex' gap='12px' justifyContent='flex-end' margin={{ top: '2' }}>
                    <Button
                      variant='secondary'
                      size='md'
                      onClick={handleCancelForm}
                      disabled={isCreating || isUpdating}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant='primary'
                      size='md'
                      onClick={handleSaveGroup}
                      disabled={isCreating || isUpdating}
                    >
                      {isCreating || isUpdating ? (
                        <Spinner size='sm' variant='primary' />
                      ) : editingId ? (
                        'Guardar cambios'
                      ) : (
                        'Crear grupo económico'
                      )}
                    </Button>
                  </Card>
                </Card>
              </FormContainer>
            )}

            {economicGroups.length === 0 && !showNewForm && editingId === null ? (
              <EmptyState>
                <Icon name='info_circle_outline' variant='neutral' modifier='regular' height={48} />
                <P type='2' variant='neutral' withMargin={false} margin={{ top: '2' }}>
                  No hay grupos económicos registrados
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  Haz clic en "Nuevo grupo económico" para agregar uno
                </P>
              </EmptyState>
            ) : filteredGroups.length === 0 && searchTerm.trim() ? (
              <EmptyState>
                <Icon name='search_outline' variant='neutral' modifier='regular' height={48} />
                <P type='2' variant='neutral' withMargin={false} margin={{ top: '2' }}>
                  No se encontraron grupos económicos
                </P>
                <P type='3' variant='neutral' withMargin={false}>
                  Intenta con otro término de búsqueda
                </P>
              </EmptyState>
            ) : (
              <ListContainer>
                <TableHeader>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    Grupo económico
                  </P>
                  <P type='2' variant='neutral-1' weight='bold' withMargin={false}>
                    Acciones
                  </P>
                </TableHeader>
                <ScrollableContainer>
                  {filteredGroups.map((group) => (
                    <TableRow key={group.id} isEditing={editingId === group.id}>
                      {editingId === group.id ? (
                        <Card padding='2'>
                          <P type='3' variant='neutral' withMargin={false}>
                            Editando...
                          </P>
                        </Card>
                      ) : (
                        <>
                          <GroupInfo>
                            <GroupNameText type='2' variant='neutral-1' withMargin={false}>
                              {group.name}
                            </GroupNameText>
                          </GroupInfo>
                          <ActionsContainer>
                            {deletingId === group.id ? (
                              <Card display='flex' gap='8px' alignItems='center'>
                                <P type='3' variant='neutral' withMargin={false}>
                                  ¿Eliminar?
                                </P>
                                <Button
                                  variant='secondary'
                                  size='sm'
                                  onClick={handleConfirmDelete}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? <Spinner size='sm' variant='primary' /> : 'Sí'}
                                </Button>
                                <Button
                                  variant='tertiary'
                                  size='sm'
                                  onClick={handleCancelDelete}
                                  disabled={isDeleting}
                                >
                                  No
                                </Button>
                              </Card>
                            ) : (
                              <>
                                <ActionButton
                                  onClick={() => handleEditGroup(group)}
                                  disabled={isDeleting || deletingId !== null}
                                  title='Editar grupo económico'
                                  type='button'
                                >
                                  <Icon name='edit_outline' variant='neutral' modifier='strong' width={20} height={20} />
                                </ActionButton>
                                <ActionButton
                                  className='delete-button'
                                  onClick={() => handleDeleteClick(group.id)}
                                  disabled={isDeleting || editingId !== null}
                                  title='Eliminar grupo económico'
                                  type='button'
                                >
                                  <Icon name='delete_outline' variant='danger' modifier='regular' width={20} height={20} />
                                </ActionButton>
                              </>
                            )}
                          </ActionsContainer>
                        </>
                      )}
                    </TableRow>
                  ))}
                </ScrollableContainer>
              </ListContainer>
            )}
          </>
        )}
      </Card>
    </Modal>
  );
};

export default EconomicGroupManagementModal;
